import { c as _c } from "react/compiler-runtime";
// Правая колонка карточки заявки: данные бизнеса и компании, контакты, где
// продаёт, оценка, теги, источник, кабинет продавца, ИИ-загрузки и дубли.
// Всё правится на месте — раньше основные поля заявки нельзя было поменять вовсе.
import { CATALOG_IMPORT_JOB_STATUS_LABELS, CRM_LEAD_SOURCES, CRM_LEAD_SOURCE_LABELS, type CatalogImportJobStatus, type CrmCompany, type CrmContact, type CrmContactInput, type CrmDuplicate, type CrmLeadAccount, type CrmLeadImportJob, type CrmLeadPatch, type CrmLeadProfile, type CrmLeadSource, type CrmLinks } from "@osmix/contracts";
import { formatNumber, formatPrice } from "@osmix/formatters";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { AlertTriangle, Building2, ExternalLink, Gauge, Globe, Mail, Phone, Plus, Sparkles, Store, Tag, Trash2, UserRound, Users, X, Pencil, Star, Route } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { SellerApplication } from "@/api";
import { useAddContact, useDeleteContact, useFillCompany, useUpdateContact, useUpdateLead } from "@/api/hooks/use-application-crm";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { errorText, formatDay, formatPhone, LINK_FIELDS, linkHref, linkLabel, mailHref, telegramHref, telHref } from "./format";
import { FieldError, InlineField, Panel, Spinner } from "./ui";
const splitList = (value: string) => value.split(/[,;\n]/).map(item => item.trim()).filter(Boolean);

/** Ссылка-иконка рядом со значением: позвонить, написать. */
function ActionLink(t0) {
  const $ = _c(8);
  const {
    href,
    label,
    icon: Icon
  } = t0;
  if (!href) {
    return null;
  }
  const external = href.startsWith("http");
  const t1 = external ? "_blank" : undefined;
  const t2 = external ? "noreferrer" : undefined;
  let t3;
  if ($[0] !== Icon) {
    t3 = <Icon className="size-3.5" />;
    $[0] = Icon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== href || $[3] !== label || $[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = <a href={href} aria-label={label} title={label} target={t1} rel={t2} className="mt-1 inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">{t3}</a>;
    $[2] = href;
    $[3] = label;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}

// ── Данные заявки и компания ────────────────────────────────────────────

export function LeadDataPanel(t0) {
  const $ = _c(101);
  const {
    application,
    profile,
    canManage
  } = t0;
  const update = useUpdateLead(application.id);
  const fill = useFillCompany(application.id);
  let t1;
  if ($[0] !== update) {
    t1 = patch => update.mutateAsync(patch);
    $[0] = update;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const save = t1;
  const inn = application.inn ?? profile.company.inn ?? "";
  const company = profile.company;
  const hasCompany = !!(company.fullName || company.shortName || company.ogrn || company.address || company.director);
  const [editingCompany, setEditingCompany] = useState(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Store className="size-4 text-muted-foreground" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = !canManage;
  let t4;
  if ($[3] !== save) {
    t4 = shopName => {
      if (!shopName) {
        throw new Error("\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u043D\u0435 \u043C\u043E\u0436\u0435\u0442 \u0431\u044B\u0442\u044C \u043F\u0443\u0441\u0442\u044B\u043C");
      }
      return save({
        shopName
      });
    };
    $[3] = save;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== application.shopName || $[6] !== t3 || $[7] !== t4) {
    t5 = <InlineField label={"\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435"} value={application.shopName} disabled={t3} onSave={t4} />;
    $[5] = application.shopName;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = !canManage;
  let t7;
  if ($[9] !== save) {
    t7 = contactName => {
      if (!contactName) {
        throw new Error("\u0423\u043A\u0430\u0436\u0438\u0442\u0435, \u0441 \u043A\u0435\u043C \u0433\u043E\u0432\u043E\u0440\u0438\u0442\u044C");
      }
      return save({
        contactName
      });
    };
    $[9] = save;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== application.contactName || $[12] !== t6 || $[13] !== t7) {
    t8 = <InlineField label={"\u041A\u043E\u043D\u0442\u0430\u043A\u0442\u043D\u043E\u0435 \u043B\u0438\u0446\u043E"} value={application.contactName} disabled={t6} onSave={t7} />;
    $[11] = application.contactName;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = application.phone ?? "";
  let t10;
  if ($[15] !== application.phone) {
    t10 = formatPhone(application.phone);
    $[15] = application.phone;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = !canManage;
  let t12;
  if ($[17] !== save) {
    t12 = phone => save({
      phone
    });
    $[17] = save;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== application.phone) {
    t13 = telHref(application.phone);
    $[19] = application.phone;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t13) {
    t14 = <ActionLink href={t13} label={"\u041F\u043E\u0437\u0432\u043E\u043D\u0438\u0442\u044C"} icon={Phone} />;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t12 || $[26] !== t14 || $[27] !== t9) {
    t15 = <InlineField label={"\u0422\u0435\u043B\u0435\u0444\u043E\u043D"} type="tel" inputMode="tel" value={t9} display={t10} disabled={t11} onSave={t12} extra={t14} />;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t14;
    $[27] = t9;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const t16 = application.email ?? "";
  const t17 = !canManage;
  let t18;
  if ($[29] !== save) {
    t18 = email => save({
      email
    });
    $[29] = save;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== application.email) {
    t19 = mailHref(application.email);
    $[31] = application.email;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t19) {
    t20 = <ActionLink href={t19} label={"\u041D\u0430\u043F\u0438\u0441\u0430\u0442\u044C \u043F\u0438\u0441\u044C\u043C\u043E"} icon={Mail} />;
    $[33] = t19;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t16 || $[36] !== t17 || $[37] !== t18 || $[38] !== t20) {
    t21 = <InlineField label={"\u041F\u043E\u0447\u0442\u0430"} type="email" inputMode="email" value={t16} disabled={t17} onSave={t18} extra={t20} />;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  const t22 = !canManage;
  let t23;
  if ($[40] !== save) {
    t23 = city => save({
      city
    });
    $[40] = save;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== application.city || $[43] !== t22 || $[44] !== t23) {
    t24 = <InlineField label={"\u0413\u043E\u0440\u043E\u0434"} value={application.city} disabled={t22} onSave={t23} />;
    $[42] = application.city;
    $[43] = t22;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== application.categories) {
    t25 = application.categories.join(", ");
    $[46] = application.categories;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  const t26 = !canManage;
  let t27;
  if ($[48] !== save) {
    t27 = value => save({
      categories: splitList(value)
    });
    $[48] = save;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== t25 || $[51] !== t26 || $[52] !== t27) {
    t28 = <InlineField label={"\u041A\u0430\u0442\u0435\u0433\u043E\u0440\u0438\u0438"} value={t25} placeholder={"\u0447\u0435\u0440\u0435\u0437 \u0437\u0430\u043F\u044F\u0442\u0443\u044E"} disabled={t26} onSave={t27} />;
    $[50] = t25;
    $[51] = t26;
    $[52] = t27;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  const t29 = !canManage;
  let t30;
  if ($[54] !== save) {
    t30 = value_0 => {
      const digits = value_0.replace(/\D/g, "");
      if (value_0 && digits.length !== 10 && digits.length !== 12) {
        throw new Error("\u0418\u041D\u041D \u2014 \u044D\u0442\u043E 10 \u0446\u0438\u0444\u0440 \u0443 \u043A\u043E\u043C\u043F\u0430\u043D\u0438\u0438 \u0438\u043B\u0438 12 \u0443 \u0418\u041F");
      }
      return save({
        inn: digits
      });
    };
    $[54] = save;
    $[55] = t30;
  } else {
    t30 = $[55];
  }
  let t31;
  if ($[56] !== inn || $[57] !== t29 || $[58] !== t30) {
    t31 = <InlineField label={"\u0418\u041D\u041D"} inputMode="numeric" value={inn} disabled={t29} onSave={t30} />;
    $[56] = inn;
    $[57] = t29;
    $[58] = t30;
    $[59] = t31;
  } else {
    t31 = $[59];
  }
  const t32 = !canManage;
  let t33;
  if ($[60] !== save) {
    t33 = comment => save({
      comment
    });
    $[60] = save;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  let t34;
  if ($[62] !== profile.comment || $[63] !== t32 || $[64] !== t33) {
    t34 = <InlineField label={"\u0417\u0430\u043C\u0435\u0442\u043A\u0430"} value={profile.comment} placeholder={"\u0434\u043B\u044F \u043A\u043E\u043B\u043B\u0435\u0433"} multiline={true} disabled={t32} onSave={t33} />;
    $[62] = profile.comment;
    $[63] = t32;
    $[64] = t33;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  let t35;
  if ($[66] !== t15 || $[67] !== t21 || $[68] !== t24 || $[69] !== t28 || $[70] !== t31 || $[71] !== t34 || $[72] !== t5 || $[73] !== t8) {
    t35 = <div className="flex flex-col">{t5}{t8}{t15}{t21}{t24}{t28}{t31}{t34}</div>;
    $[66] = t15;
    $[67] = t21;
    $[68] = t24;
    $[69] = t28;
    $[70] = t31;
    $[71] = t34;
    $[72] = t5;
    $[73] = t8;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  let t36;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wider text-muted-foreground uppercase"><Building2 className="size-3.5" />Компания</p>;
    $[75] = t36;
  } else {
    t36 = $[75];
  }
  let t37;
  if ($[76] !== canManage || $[77] !== editingCompany || $[78] !== fill || $[79] !== hasCompany || $[80] !== inn) {
    t37 = canManage && !editingCompany && <div className="flex flex-wrap justify-end gap-1"><Button size="xs" variant="ghost" onClick={() => {
        fill.reset();
        setEditingCompany(true);
      }}><Pencil />{hasCompany ? "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C" : "\u0412\u0440\u0443\u0447\u043D\u0443\u044E"}</Button><Button size="xs" variant="outline" disabled={fill.isPending || !inn} title={inn ? "\u041F\u043E\u0434\u0442\u044F\u043D\u0443\u0442\u044C \u0440\u0435\u043A\u0432\u0438\u0437\u0438\u0442\u044B \u0438\u0437 DaData" : "\u0421\u043D\u0430\u0447\u0430\u043B\u0430 \u0443\u043A\u0430\u0436\u0438\u0442\u0435 \u0418\u041D\u041D"} onClick={() => fill.mutate()}>{fill.isPending ? <Spinner className="size-3" /> : <Sparkles />}Заполнить по ИНН</Button></div>;
    $[76] = canManage;
    $[77] = editingCompany;
    $[78] = fill;
    $[79] = hasCompany;
    $[80] = inn;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== t37) {
    t38 = <div className="flex items-center justify-between gap-2">{t36}{t37}</div>;
    $[82] = t37;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== fill.error) {
    t39 = fill.error ? errorText(fill.error) : null;
    $[84] = fill.error;
    $[85] = t39;
  } else {
    t39 = $[85];
  }
  let t40;
  if ($[86] !== t39) {
    t40 = <FieldError>{t39}</FieldError>;
    $[86] = t39;
    $[87] = t40;
  } else {
    t40 = $[87];
  }
  let t41;
  if ($[88] !== company || $[89] !== editingCompany || $[90] !== hasCompany || $[91] !== inn || $[92] !== update) {
    t41 = editingCompany ? <CompanyForm company={company} busy={update.isPending} onCancel={() => setEditingCompany(false)} onSubmit={async next => {
      await update.mutateAsync({
        company: next
      });
      setEditingCompany(false);
    }} /> : hasCompany ? <CompanyDetails company={company} /> : <p className="text-xs text-muted-foreground">{inn ? "\u0420\u0435\u043A\u0432\u0438\u0437\u0438\u0442\u043E\u0432 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442 \u2014 \u043F\u043E\u0434\u0442\u044F\u043D\u0438\u0442\u0435 \u0438\u0445 \u043F\u043E \u0418\u041D\u041D." : "\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u0418\u041D\u041D \u2014 \u0440\u0435\u043A\u0432\u0438\u0437\u0438\u0442\u044B \u043F\u043E\u0434\u0442\u044F\u043D\u0443\u0442\u0441\u044F \u0438\u0437 DaData."}</p>;
    $[88] = company;
    $[89] = editingCompany;
    $[90] = hasCompany;
    $[91] = inn;
    $[92] = update;
    $[93] = t41;
  } else {
    t41 = $[93];
  }
  let t42;
  if ($[94] !== t38 || $[95] !== t40 || $[96] !== t41) {
    t42 = <div className="mt-3 flex flex-col gap-2 rounded-lg border bg-muted/20 p-3">{t38}{t40}{t41}</div>;
    $[94] = t38;
    $[95] = t40;
    $[96] = t41;
    $[97] = t42;
  } else {
    t42 = $[97];
  }
  let t43;
  if ($[98] !== t35 || $[99] !== t42) {
    t43 = <Panel title={"\u0414\u0430\u043D\u043D\u044B\u0435 \u0437\u0430\u044F\u0432\u043A\u0438"} icon={t2} data-testid="crm-lead-data">{t35}{t42}</Panel>;
    $[98] = t35;
    $[99] = t42;
    $[100] = t43;
  } else {
    t43 = $[100];
  }
  return t43;
}
const COMPANY_FIELDS: {
  key: keyof CrmCompany;
  label: string;
  placeholder?: string;
}[] = [{
  key: "shortName",
  label: "Короткое имя",
  placeholder: "ООО «Ромашка», ИП Иванов И. И."
}, {
  key: "fullName",
  label: "Полное имя"
}, {
  key: "orgForm",
  label: "Форма",
  placeholder: "ООО, ИП, самозанятый"
}, {
  key: "ogrn",
  label: "ОГРН"
}, {
  key: "kpp",
  label: "КПП"
}, {
  key: "address",
  label: "Адрес"
}, {
  key: "director",
  label: "Руководитель"
}, {
  key: "state",
  label: "Статус",
  placeholder: "Действующая"
}, {
  key: "okved",
  label: "ОКВЭД"
}];

/**
 * Реквизиты руками — когда DaData не подключена или не знает компанию.
 * `filledFrom`/`filledAt` не отправляем: «вручную» ставит шлюз.
 */
function CompanyForm(t0) {
  const $ = _c(25);
  const {
    company,
    busy,
    onSubmit,
    onCancel
  } = t0;
  let t1;
  if ($[0] !== company) {
    t1 = () => Object.fromEntries(COMPANY_FIELDS.map(t2 => {
      const {
        key
      } = t2;
      return [key, String(company[key] ?? "")];
    }));
    $[0] = company;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [draft, setDraft] = useState(t1);
  const [error, setError] = useState(null);
  let t2;
  if ($[2] !== draft || $[3] !== onSubmit) {
    t2 = async event => {
      event.preventDefault();
      setError(null);
      const next = {};
      for (const {
        key: key_0
      } of COMPANY_FIELDS) {
        (next as Record<string, string>)[key_0] = draft[key_0]?.trim() ?? "";
      }
      ;
      try {
        await onSubmit(next);
      } catch (t3) {
        const e = t3;
        setError(errorText(e));
      }
    };
    $[2] = draft;
    $[3] = onSubmit;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== draft) {
    t3 = COMPANY_FIELDS.map(t4 => {
      const {
        key: key_1,
        label,
        placeholder
      } = t4;
      return <label key={key_1} className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-x-3 text-xs sm:grid-cols-[8rem_minmax(0,1fr)]"><span className="text-muted-foreground">{label}</span><Input className="h-8" aria-label={label} placeholder={placeholder} value={draft[key_1] ?? ""} onChange={e_0 => setDraft(current => ({
          ...current,
          [key_1]: e_0.target.value
        }))} /></label>;
    });
    $[5] = draft;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== error) {
    t4 = <FieldError>{error}</FieldError>;
    $[7] = error;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== busy) {
    t5 = busy && <Spinner />;
    $[9] = busy;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== busy || $[12] !== t5) {
    t6 = <Button type="submit" size="sm" disabled={busy}>{t5}Сохранить</Button>;
    $[11] = busy;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== busy || $[15] !== onCancel) {
    t7 = <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onCancel}>Отмена</Button>;
    $[14] = busy;
    $[15] = onCancel;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className="flex gap-2">{t6}{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t2 || $[21] !== t3 || $[22] !== t4 || $[23] !== t8) {
    t9 = <form className="flex flex-col gap-2" onSubmit={t2}>{t3}{t4}{t8}</form>;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}

/** ГГГГ-ММ-ДД из DaData → ДД.ММ.ГГГГ без перевода в Date: в другом поясе съехал бы день. */
const isoDay = (value: string) => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : formatDay(value);
};
function CompanyDetails(t0) {
  const $ = _c(38);
  const {
    company
  } = t0;
  let t1;
  if ($[0] !== company.state) {
    t1 = company.state ? /действ/i.test(company.state) : null;
    $[0] = company.state;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const active = t1;
  const t2 = company.fullName || company.shortName;
  let t3;
  if ($[2] !== t2) {
    t3 = ["\u041F\u043E\u043B\u043D\u043E\u0435 \u0438\u043C\u044F", t2];
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== company.ogrn) {
    t4 = ["\u041E\u0413\u0420\u041D", company.ogrn];
    $[4] = company.ogrn;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== company.kpp) {
    t5 = ["\u041A\u041F\u041F", company.kpp];
    $[6] = company.kpp;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== company.address) {
    t6 = ["\u0410\u0434\u0440\u0435\u0441", company.address];
    $[8] = company.address;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== company.director) {
    t7 = ["\u0420\u0443\u043A\u043E\u0432\u043E\u0434\u0438\u0442\u0435\u043B\u044C", company.director];
    $[10] = company.director;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== company.registeredAt) {
    t8 = company.registeredAt ? isoDay(company.registeredAt) : undefined;
    $[12] = company.registeredAt;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = ["\u0420\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044F", t8];
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== company.okved) {
    t10 = ["\u041E\u041A\u0412\u042D\u0414", company.okved];
    $[16] = company.okved;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t3 || $[20] !== t4 || $[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== t9) {
    t11 = [t3, t4, t5, t6, t7, t9, t10];
    $[18] = t10;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const rows = t11;
  let t12;
  if ($[26] !== active || $[27] !== company.state) {
    t12 = company.state && <Badge variant="outline" className={cn("self-start font-normal", active ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "border-destructive/40 bg-destructive/10 text-destructive")}>{company.state}</Badge>;
    $[26] = active;
    $[27] = company.state;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== rows) {
    t13 = <dl className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs sm:grid-cols-[8rem_minmax(0,1fr)]">{rows.filter(_temp).map(_temp2)}</dl>;
    $[29] = rows;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== company.filledAt || $[32] !== company.filledFrom) {
    t14 = company.filledAt && <p className="text-[11px] text-muted-foreground">{company.filledFrom === "dadata" ? "\u041F\u043E \u0434\u0430\u043D\u043D\u044B\u043C DaData" : "\u0417\u0430\u043F\u043E\u043B\u043D\u0435\u043D\u043E \u0432\u0440\u0443\u0447\u043D\u0443\u044E"} · {formatDay(company.filledAt)}</p>;
    $[31] = company.filledAt;
    $[32] = company.filledFrom;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t12 || $[35] !== t13 || $[36] !== t14) {
    t15 = <div className="flex flex-col gap-1.5">{t12}{t13}{t14}</div>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
}

// ── Контакты ────────────────────────────────────────────────────────────
function _temp2(t0) {
  const [label, value_0] = t0;
  return <div key={label} className="contents"><dt className="text-muted-foreground">{label}</dt><dd className="break-words">{value_0}</dd></div>;
}
function _temp(t0) {
  const [, value] = t0;
  return value;
}
export function ContactsPanel(t0) {
  const $ = _c(44);
  const {
    applicationId,
    contacts,
    canManage,
    main
  } = t0;
  const add = useAddContact(applicationId);
  const update = useUpdateContact(applicationId);
  const remove = useDeleteContact(applicationId);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Users className="size-4 text-muted-foreground" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = contacts.length + (main?.name ? 1 : 0);
  let t3;
  if ($[1] !== canManage || $[2] !== editing) {
    t3 = canManage && editing !== "new" ? <Button size="xs" variant="ghost" onClick={() => setEditing("new")}><Plus className="size-3.5" />Контакт</Button> : null;
    $[1] = canManage;
    $[2] = editing;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== add || $[5] !== editing) {
    t4 = editing === "new" && <ContactForm busy={add.isPending} onCancel={() => setEditing(null)} onSubmit={async input => {
      await add.mutateAsync(input);
      setEditing(null);
    }} />;
    $[4] = add;
    $[5] = editing;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== main) {
    t5 = main?.name && <div className="flex items-start gap-2 rounded-lg border border-dashed p-2.5"><span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"><UserRound className="size-3.5" /></span><div className="min-w-0 flex-1"><p className="flex flex-wrap items-center gap-1.5 text-sm font-medium"><span className="break-words">{main.name}</span><Badge variant="outline" className="text-[10px]">из заявки</Badge></p><div className="mt-1 flex flex-col gap-0.5 text-xs">{main.phone && <a href={telHref(main.phone) ?? undefined} className="tabular-nums hover:underline">{formatPhone(main.phone)}</a>}{main.email && <a href={mailHref(main.email) ?? undefined} className="break-all hover:underline">{main.email}</a>}</div><p className="mt-1 text-xs text-muted-foreground">Правится в «Данных заявки»</p></div></div>;
    $[7] = main;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== contacts.length || $[10] !== editing || $[11] !== main?.name) {
    t6 = contacts.length === 0 && !main?.name && editing !== "new" && <p className="text-sm text-muted-foreground">Контактов нет</p>;
    $[9] = contacts.length;
    $[10] = editing;
    $[11] = main?.name;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== canManage || $[14] !== contacts || $[15] !== editing || $[16] !== update) {
    let t8;
    if ($[18] !== canManage || $[19] !== editing || $[20] !== update) {
      t8 = item => editing === item.id ? <ContactForm key={item.id} initial={item} busy={update.isPending} onCancel={() => setEditing(null)} onSubmit={async input_0 => {
        await update.mutateAsync({
          contactId: item.id,
          patch: input_0
        });
        setEditing(null);
      }} /> : <div key={item.id} className="group flex items-start gap-2 rounded-lg border p-2.5"><span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><UserRound className="size-3.5" /></span><div className="min-w-0 flex-1"><p className="flex flex-wrap items-center gap-1.5 text-sm font-medium"><span className="break-words">{item.name}</span>{item.isPrimary && <Badge variant="secondary" className="text-[10px]">основной</Badge>}</p>{item.role && <p className="text-xs text-muted-foreground">{item.role}</p>}<div className="mt-1 flex flex-col gap-0.5 text-xs">{item.phone && <a href={telHref(item.phone) ?? undefined} className="tabular-nums hover:underline">{formatPhone(item.phone)}</a>}{item.email && <a href={mailHref(item.email) ?? undefined} className="break-all hover:underline">{item.email}</a>}{item.telegram && <a href={telegramHref(item.telegram) ?? undefined} target="_blank" rel="noreferrer" className="break-all hover:underline">Telegram @{item.telegram.replace(/^@/, "").replace(/^https?:\/\/t\.me\//, "")}</a>}</div>{canManage && !item.isPrimary && <button type="button" className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => update.mutate({
            contactId: item.id,
            patch: {
              isPrimary: true
            }
          }, {
            onError: e => setError(errorText(e))
          })}><Star className="size-3" />Сделать основным</button>}</div>{canManage && <div className="flex shrink-0 gap-0.5 md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:focus-within:opacity-100"><Button size="icon-xs" variant="ghost" aria-label={`Изменить контакт ${item.name}`} onClick={() => setEditing(item.id)}><Pencil /></Button><Button size="icon-xs" variant="ghost" aria-label={`Удалить контакт ${item.name}`} className="hover:text-destructive" onClick={() => setDeleting(item)}><Trash2 /></Button></div>}</div>;
      $[18] = canManage;
      $[19] = editing;
      $[20] = update;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    t7 = contacts.map(t8);
    $[13] = canManage;
    $[14] = contacts;
    $[15] = editing;
    $[16] = update;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[22] !== error) {
    t8 = <FieldError>{error}</FieldError>;
    $[22] = error;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t4 || $[25] !== t5 || $[26] !== t6 || $[27] !== t7 || $[28] !== t8) {
    t9 = <div className="flex flex-col gap-2">{t4}{t5}{t6}{t7}{t8}</div>;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const t10 = !!deleting;
  const t11 = deleting ? `${deleting.name} пропадёт из карточки заявки.` : undefined;
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setDeleting(null);
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== deleting || $[32] !== remove) {
    t13 = () => deleting && remove.mutate(deleting.id, {
      onSettled: () => setDeleting(null),
      onError: e_0 => setError(errorText(e_0))
    });
    $[31] = deleting;
    $[32] = remove;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== remove.isPending || $[35] !== t10 || $[36] !== t11 || $[37] !== t13) {
    t14 = <ConfirmDialog open={t10} title={"\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u043A\u043E\u043D\u0442\u0430\u043A\u0442?"} description={t11} confirmLabel={"\u0423\u0434\u0430\u043B\u0438\u0442\u044C"} confirmVariant="destructive" busy={remove.isPending} onClose={t12} onConfirm={t13} />;
    $[34] = remove.isPending;
    $[35] = t10;
    $[36] = t11;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t14 || $[40] !== t2 || $[41] !== t3 || $[42] !== t9) {
    t15 = <Panel title={"\u041A\u043E\u043D\u0442\u0430\u043A\u0442\u044B"} icon={t1} count={t2} action={t3}>{t9}{t14}</Panel>;
    $[39] = t14;
    $[40] = t2;
    $[41] = t3;
    $[42] = t9;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  return t15;
}
function ContactForm(t0) {
  const $ = _c(52);
  const {
    initial,
    busy,
    onSubmit,
    onCancel
  } = t0;
  const [name, setName] = useState(initial?.name ?? "");
  const [role, setRole] = useState(initial?.role ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [telegram, setTelegram] = useState(initial?.telegram ?? "");
  const [isPrimary, setIsPrimary] = useState(initial?.isPrimary ?? false);
  const [error, setError] = useState(null);
  let t1;
  if ($[0] !== email || $[1] !== isPrimary || $[2] !== name || $[3] !== onSubmit || $[4] !== phone || $[5] !== role || $[6] !== telegram) {
    t1 = async event => {
      event.preventDefault();
      if (!name.trim()) {
        setError("\u0423\u043A\u0430\u0436\u0438\u0442\u0435 \u0438\u043C\u044F");
        return;
      }
      setError(null);
      ;
      try {
        await onSubmit({
          name: name.trim(),
          role: role.trim(),
          phone: phone.trim(),
          email: email.trim(),
          telegram: telegram.trim().replace(/^@/, ""),
          isPrimary
        });
      } catch (t2) {
        const e = t2;
        setError(errorText(e));
      }
    };
    $[0] = email;
    $[1] = isPrimary;
    $[2] = name;
    $[3] = onSubmit;
    $[4] = phone;
    $[5] = role;
    $[6] = telegram;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = e_0 => setName(e_0.target.value);
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== name) {
    t3 = <Input aria-label={"\u0418\u043C\u044F"} placeholder={"\u0418\u043C\u044F"} className="h-8" autoFocus={true} value={name} onChange={t2} />;
    $[9] = name;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e_1 => setRole(e_1.target.value);
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== role) {
    t5 = <Input aria-label={"\u0420\u043E\u043B\u044C"} placeholder={"\u0420\u043E\u043B\u044C: \u0441\u043E\u0431\u0441\u0442\u0432\u0435\u043D\u043D\u0438\u043A, \u043C\u0435\u043D\u0435\u0434\u0436\u0435\u0440\u2026"} className="h-8" value={role} onChange={t4} />;
    $[12] = role;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_2 => setPhone(e_2.target.value);
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== phone) {
    t7 = <Input aria-label={"\u0422\u0435\u043B\u0435\u0444\u043E\u043D"} placeholder={"\u0422\u0435\u043B\u0435\u0444\u043E\u043D"} type="tel" inputMode="tel" className="h-8" value={phone} onChange={t6} />;
    $[15] = phone;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e_3 => setEmail(e_3.target.value);
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== email) {
    t9 = <Input aria-label={"\u041F\u043E\u0447\u0442\u0430"} placeholder={"\u041F\u043E\u0447\u0442\u0430"} type="email" inputMode="email" className="h-8" value={email} onChange={t8} />;
    $[18] = email;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e_4 => setTelegram(e_4.target.value);
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== telegram) {
    t11 = <Input aria-label="Telegram" placeholder={"Telegram: @\u043D\u0438\u043A"} className="h-8" value={telegram} onChange={t10} />;
    $[21] = telegram;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e_5 => setIsPrimary(e_5.target.checked);
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== isPrimary) {
    t13 = <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="size-4 accent-primary" checked={isPrimary} onChange={t12} />Основной контакт</label>;
    $[24] = isPrimary;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t11 || $[27] !== t13 || $[28] !== t3 || $[29] !== t5 || $[30] !== t7 || $[31] !== t9) {
    t14 = <div className="grid gap-2 sm:grid-cols-2">{t3}{t5}{t7}{t9}{t11}{t13}</div>;
    $[26] = t11;
    $[27] = t13;
    $[28] = t3;
    $[29] = t5;
    $[30] = t7;
    $[31] = t9;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== error) {
    t15 = <FieldError>{error}</FieldError>;
    $[33] = error;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== busy) {
    t16 = busy && <Spinner />;
    $[35] = busy;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const t17 = initial ? "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C" : "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C";
  let t18;
  if ($[37] !== busy || $[38] !== t16 || $[39] !== t17) {
    t18 = <Button type="submit" size="sm" disabled={busy}>{t16}{t17}</Button>;
    $[37] = busy;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== busy || $[42] !== onCancel) {
    t19 = <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onCancel}>Отмена</Button>;
    $[41] = busy;
    $[42] = onCancel;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t18 || $[45] !== t19) {
    t20 = <div className="flex gap-2">{t18}{t19}</div>;
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t1 || $[48] !== t14 || $[49] !== t15 || $[50] !== t20) {
    t21 = <form className="flex flex-col gap-2 rounded-lg border bg-muted/20 p-3" onSubmit={t1}>{t14}{t15}{t20}</form>;
    $[47] = t1;
    $[48] = t14;
    $[49] = t15;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  return t21;
}

// ── Где продаёт ─────────────────────────────────────────────────────────

export function LinksPanel({
  applicationId,
  links,
  canManage
}: {
  applicationId: string;
  links: CrmLinks;
  canManage: boolean;
}) {
  const update = useUpdateLead(applicationId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CrmLinks>(links);
  const [error, setError] = useState<string | null>(null);
  const filled = LINK_FIELDS.filter(field => links[field.key]?.trim());
  return <Panel title="Где продаёт" icon={<Globe className="size-4 text-muted-foreground" />} action={canManage && !editing ? <Button size="xs" variant="ghost" onClick={() => {
    setDraft(links);
    setError(null);
    setEditing(true);
  }}>
            <Pencil className="size-3.5" />
            Изменить
          </Button> : null}>
      {editing ? <form className="flex flex-col gap-2" onSubmit={async event => {
      event.preventDefault();
      setError(null);
      try {
        const clean: CrmLinks = {};
        for (const [key, value] of Object.entries(draft) as [keyof CrmLinks, string | undefined][]) {
          if (value?.trim()) clean[key] = value.trim();
        }
        await update.mutateAsync({
          links: clean
        });
        setEditing(false);
      } catch (e) {
        setError(errorText(e));
      }
    }}>
          {LINK_FIELDS.map(field_0 => <label key={field_0.key} className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-x-3 text-xs sm:grid-cols-[8rem_minmax(0,1fr)]">
              <span className="text-muted-foreground">{field_0.label}</span>
              <Input className="h-8" aria-label={field_0.label} placeholder={field_0.base ? "@ник или ссылка" : "ссылка"} value={draft[field_0.key] ?? ""} onChange={e_0 => setDraft(current => ({
          ...current,
          [field_0.key]: e_0.target.value
        }))} />
            </label>)}
          <label className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-x-3 text-xs sm:grid-cols-[8rem_minmax(0,1fr)]">
            <span className="text-muted-foreground">Другое</span>
            <Input className="h-8" aria-label="Другое" placeholder="Ярмарка Мастеров, Lamoda…" value={draft.other ?? ""} onChange={e_1 => setDraft(current_0 => ({
          ...current_0,
          other: e_1.target.value
        }))} />
          </label>
          <FieldError>{error}</FieldError>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={update.isPending}>
              {update.isPending && <Spinner />}
              Сохранить
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Отмена
            </Button>
          </div>
        </form> : filled.length === 0 && !links.other ? <p className="text-sm text-muted-foreground">Сайт, соцсети и маркетплейсы не указаны</p> : <ul className="flex flex-col gap-1.5 text-sm">
          {filled.map(field_1 => {
        const href = linkHref(field_1.key, links[field_1.key]);
        return <li key={field_1.key} className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-3 sm:grid-cols-[8rem_minmax(0,1fr)]">
                <span className="text-xs leading-5 text-muted-foreground">{field_1.label}</span>
                {href ? <a href={href} target="_blank" rel="noreferrer" className="inline-flex min-w-0 items-center gap-1 text-primary hover:underline">
                    <span className="truncate">{linkLabel(links[field_1.key] ?? "")}</span>
                    <ExternalLink className="size-3 shrink-0" />
                  </a> : null}
              </li>;
      })}
          {links.other && <li className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-3 sm:grid-cols-[8rem_minmax(0,1fr)]">
              <span className="text-xs leading-5 text-muted-foreground">Другое</span>
              <span className="break-words">{links.other}</span>
            </li>}
        </ul>}
    </Panel>;
}

// ── Оценка ──────────────────────────────────────────────────────────────

/** «12 000», «12 000 руб.», «1,5 млн» — ввод с пробелами и запятой. */
const parseAmount = (value: string): number | null => {
  const clean = value.replace(/\s/g, "").replace(",", ".");
  if (!clean) return null;
  const parsed = Number(clean);
  if (!Number.isFinite(parsed) || parsed < 0) throw new Error("Нужно число");
  return parsed;
};
export function EstimatePanel(t0) {
  const $ = _c(30);
  const {
    applicationId,
    profile,
    canManage
  } = t0;
  const update = useUpdateLead(applicationId);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Gauge className="size-4 text-muted-foreground" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = profile.assortmentSize === null ? "" : String(profile.assortmentSize);
  const t3 = profile.assortmentSize === null ? undefined : `${formatNumber(profile.assortmentSize)} SKU`;
  const t4 = !canManage;
  let t5;
  if ($[1] !== update) {
    t5 = value => {
      const parsed = parseAmount(value);
      return update.mutateAsync({
        assortmentSize: parsed === null ? null : Math.round(parsed)
      });
    };
    $[1] = update;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== t2 || $[4] !== t3 || $[5] !== t4 || $[6] !== t5) {
    t6 = <InlineField label={"\u0410\u0441\u0441\u043E\u0440\u0442\u0438\u043C\u0435\u043D\u0442"} inputMode="numeric" value={t2} display={t3} placeholder={"\u0441\u043A\u043E\u043B\u044C\u043A\u043E SKU"} disabled={t4} onSave={t5} />;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = profile.avgCheck === null ? "" : String(profile.avgCheck);
  let t8;
  if ($[8] !== profile.avgCheck) {
    t8 = profile.avgCheck === null ? undefined : formatPrice(profile.avgCheck);
    $[8] = profile.avgCheck;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = !canManage;
  let t10;
  if ($[10] !== update) {
    t10 = value_0 => update.mutateAsync({
      avgCheck: parseAmount(value_0)
    });
    $[10] = update;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== t7 || $[14] !== t8 || $[15] !== t9) {
    t11 = <InlineField label={"\u0421\u0440\u0435\u0434\u043D\u0438\u0439 \u0447\u0435\u043A"} inputMode="decimal" value={t7} display={t8} placeholder={"\u0440\u0443\u0431."} disabled={t9} onSave={t10} />;
    $[12] = t10;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = profile.monthlyPotential === null ? "" : String(profile.monthlyPotential);
  let t13;
  if ($[17] !== profile.monthlyPotential) {
    t13 = profile.monthlyPotential === null ? undefined : formatPrice(profile.monthlyPotential);
    $[17] = profile.monthlyPotential;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const t14 = !canManage;
  let t15;
  if ($[19] !== update) {
    t15 = value_1 => update.mutateAsync({
      monthlyPotential: parseAmount(value_1)
    });
    $[19] = update;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== t12 || $[22] !== t13 || $[23] !== t14 || $[24] !== t15) {
    t16 = <InlineField label={"\u041F\u043E\u0442\u0435\u043D\u0446\u0438\u0430\u043B \u0432 \u043C\u0435\u0441\u044F\u0446"} inputMode="decimal" value={t12} display={t13} placeholder={"\u043E\u0431\u043E\u0440\u043E\u0442, \u0440\u0443\u0431."} disabled={t14} onSave={t15} />;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t11 || $[27] !== t16 || $[28] !== t6) {
    t17 = <Panel title={"\u041E\u0446\u0435\u043D\u043A\u0430"} icon={t1}>{t6}{t11}{t16}</Panel>;
    $[26] = t11;
    $[27] = t16;
    $[28] = t6;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  return t17;
}

// ── Теги ────────────────────────────────────────────────────────────────

export function TagsPanel(t0) {
  const $ = _c(29);
  const {
    applicationId,
    tags,
    suggestions,
    canManage
  } = t0;
  const update = useUpdateLead(applicationId);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState(null);
  const listId = `tags-${applicationId}`;
  let t1;
  if ($[0] !== update) {
    t1 = async next => {
      setError(null);
      ;
      try {
        await update.mutateAsync({
          tags: next
        });
      } catch (t2) {
        const e = t2;
        setError(errorText(e));
      }
    };
    $[0] = update;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const save = t1;
  let t2;
  if ($[2] !== draft || $[3] !== save || $[4] !== tags) {
    t2 = async () => {
      const value = draft.trim();
      if (!value) {
        return;
      }
      if (tags.some(tag => tag.toLowerCase() === value.toLowerCase())) {
        setDraft("");
        return;
      }
      await save([...tags, value]);
      setDraft("");
    };
    $[2] = draft;
    $[3] = save;
    $[4] = tags;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const addTag = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Tag className="size-4 text-muted-foreground" />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== canManage || $[8] !== save || $[9] !== tags || $[10] !== update) {
    t4 = tags.length > 0 ? <div className="flex flex-wrap gap-1.5">{tags.map(tag_0 => <span key={tag_0} className="inline-flex items-center gap-1 rounded-full border bg-muted/40 py-0.5 pr-1 pl-2.5 text-xs">{tag_0}{canManage && <button type="button" aria-label={`Убрать тег ${tag_0}`} disabled={update.isPending} className="rounded-full p-0.5 text-muted-foreground hover:text-foreground" onClick={() => void save(tags.filter(item => item !== tag_0))}><X className="size-3" /></button>}</span>)}</div> : <p className="text-sm text-muted-foreground">Тегов нет</p>;
    $[7] = canManage;
    $[8] = save;
    $[9] = tags;
    $[10] = update;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== addTag || $[13] !== canManage || $[14] !== draft || $[15] !== listId || $[16] !== suggestions || $[17] !== tags || $[18] !== update) {
    t5 = canManage && <form className="flex gap-2" onSubmit={event => {
      event.preventDefault();
      addTag();
    }}><Input aria-label={"\u041D\u043E\u0432\u044B\u0439 \u0442\u0435\u0433"} placeholder={"\u041D\u043E\u0432\u044B\u0439 \u0442\u0435\u0433"} className="h-8" list={listId} maxLength={40} value={draft} onChange={e_0 => setDraft(e_0.target.value)} /><datalist id={listId}>{suggestions.filter(item_0 => !tags.includes(item_0)).map(_temp3)}</datalist><Button type="submit" size="sm" variant="outline" disabled={!draft.trim() || update.isPending}>Добавить</Button></form>;
    $[12] = addTag;
    $[13] = canManage;
    $[14] = draft;
    $[15] = listId;
    $[16] = suggestions;
    $[17] = tags;
    $[18] = update;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== error) {
    t6 = <FieldError>{error}</FieldError>;
    $[20] = error;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = <div className="flex flex-col gap-2">{t4}{t5}{t6}</div>;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t7 || $[27] !== tags.length) {
    t8 = <Panel title={"\u0422\u0435\u0433\u0438"} icon={t3} count={tags.length}>{t7}</Panel>;
    $[26] = t7;
    $[27] = tags.length;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
}

// ── Источник ────────────────────────────────────────────────────────────
function _temp3(item_1) {
  return <option key={item_1} value={item_1} />;
}
export function SourcePanel(t0) {
  const $ = _c(24);
  const {
    applicationId,
    profile,
    submittedAt,
    canManage
  } = t0;
  const update = useUpdateLead(applicationId);
  const [error, setError] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Route className="size-4 text-muted-foreground" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="text-xs text-muted-foreground">Откуда</span>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== canManage || $[3] !== profile.source || $[4] !== update) {
    t3 = <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-x-3 py-1 text-sm sm:grid-cols-[8rem_minmax(0,1fr)]">{t2}{canManage ? <Select aria-label={"\u0418\u0441\u0442\u043E\u0447\u043D\u0438\u043A"} className="h-8" value={profile.source} disabled={update.isPending} onChange={e => {
        setError(null);
        update.mutateAsync({
          source: e.target.value as CrmLeadSource
        }).catch(err => setError(errorText(err)));
      }}>{CRM_LEAD_SOURCES.map(_temp4)}</Select> : <span>{CRM_LEAD_SOURCE_LABELS[profile.source]}</span>}</div>;
    $[2] = canManage;
    $[3] = profile.source;
    $[4] = update;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = !canManage;
  let t5;
  if ($[6] !== update) {
    t5 = sourceDetail => update.mutateAsync({
      sourceDetail
    });
    $[6] = update;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== profile.sourceDetail || $[9] !== t4 || $[10] !== t5) {
    t6 = <InlineField label={"\u0423\u0442\u043E\u0447\u043D\u0435\u043D\u0438\u0435"} value={profile.sourceDetail} placeholder={"\u0432\u044B\u0441\u0442\u0430\u0432\u043A\u0430, \u043A\u0442\u043E \u043F\u043E\u0440\u0435\u043A\u043E\u043C\u0435\u043D\u0434\u043E\u0432\u0430\u043B"} disabled={t4} onSave={t5} />;
    $[8] = profile.sourceDetail;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="pt-0.5 text-xs text-muted-foreground">Подана</span>;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== submittedAt) {
    t8 = formatDay(submittedAt);
    $[13] = submittedAt;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-3 py-1 text-sm sm:grid-cols-[8rem_minmax(0,1fr)]">{t7}<span>{t8}</span></div>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== error) {
    t10 = <FieldError>{error}</FieldError>;
    $[17] = error;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t3 || $[21] !== t6 || $[22] !== t9) {
    t11 = <Panel title={"\u0418\u0441\u0442\u043E\u0447\u043D\u0438\u043A"} icon={t1}><div className="flex flex-col">{t3}{t6}{t9}{t10}</div></Panel>;
    $[19] = t10;
    $[20] = t3;
    $[21] = t6;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}

// ── Кабинет продавца и ИИ-загрузки ──────────────────────────────────────
function _temp4(source) {
  return <option key={source} value={source}>{CRM_LEAD_SOURCE_LABELS[source]}</option>;
}
export function AccountPanel(t0) {
  const $ = _c(5);
  const {
    account,
    onCreate
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <UserRound className="size-4 text-muted-foreground" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== account || $[2] !== onCreate || $[3] !== t) {
    t2 = <Panel title={"\u041A\u0430\u0431\u0438\u043D\u0435\u0442 \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u0430"} icon={t1} data-testid="crm-account">{account ? <div className="flex flex-col gap-2 text-sm"><dl className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 sm:grid-cols-[8rem_minmax(0,1fr)]"><dt className="text-xs leading-5 text-muted-foreground">Почта</dt><dd className="break-all">{account.email || "\u2014"}</dd><dt className="text-xs leading-5 text-muted-foreground">Статус</dt><dd>{t(`onboarding.${account.onboardingStatus}`, {
              defaultValue: account.onboardingStatus
            })}</dd><dt className="text-xs leading-5 text-muted-foreground">Товаров</dt><dd className="tabular-nums">{formatNumber(account.productsCount)}<span className="text-muted-foreground"> · опубликовано {formatNumber(account.publishedProductsCount)}</span></dd></dl><Button size="sm" variant="outline" className="self-start" asChild={true}><Link to={`/shops/seller/${account.userId}`}><ExternalLink className="size-4" />Карточка продавца</Link></Button></div> : <div className="flex flex-col gap-2"><p className="text-sm text-muted-foreground">Кабинета ещё нет. Его можно завести по телефону из заявки.</p>{onCreate && <Button size="sm" variant="outline" className="self-start" onClick={onCreate}>Создать кабинет продавца</Button>}</div>}</Panel>;
    $[1] = account;
    $[2] = onCreate;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
const JOB_TONE: Partial<Record<CatalogImportJobStatus, string>> = {
  done: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  failed: "border-destructive/40 bg-destructive/10 text-destructive",
  review: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-500"
};
export function ImportsPanel(t0) {
  const $ = _c(9);
  const {
    jobs,
    onNew
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Sparkles className="size-4 text-muted-foreground" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onNew) {
    t2 = onNew ? <Button size="xs" variant="ghost" onClick={onNew}><Plus className="size-3.5" />Новая</Button> : null;
    $[1] = onNew;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== jobs) {
    t3 = jobs.length === 0 ? <p className="text-sm text-muted-foreground">По этой заявке каталог ещё не загружали</p> : <ul className="flex flex-col divide-y rounded-lg border">{jobs.map(_temp5)}</ul>;
    $[3] = jobs;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== jobs.length || $[6] !== t2 || $[7] !== t3) {
    t4 = <Panel title={"\u0418\u0418-\u0437\u0430\u0433\u0440\u0443\u0437\u043A\u0438 \u043A\u0430\u0442\u0430\u043B\u043E\u0433\u0430"} icon={t1} count={jobs.length} action={t2}>{t3}</Panel>;
    $[5] = jobs.length;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}

// ── Дубли ───────────────────────────────────────────────────────────────
function _temp5(job) {
  const status = job.status as CatalogImportJobStatus;
  return <li key={job.id}><Link to={`/catalog-import/${job.id}`} className="flex items-start gap-2 px-2.5 py-2 transition-colors hover:bg-muted/40"><div className="min-w-0 flex-1"><p className="truncate text-sm">{job.title || "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u043A\u0430\u0442\u0430\u043B\u043E\u0433\u0430"}</p><p className="text-xs text-muted-foreground">{formatDay(job.createdAt)} · загружено {job.itemsCommitted} из {job.itemsTotal}</p></div><Badge variant="outline" className={cn("shrink-0 font-normal", JOB_TONE[status])}>{CATALOG_IMPORT_JOB_STATUS_LABELS[status] ?? job.status}</Badge></Link></li>;
}
const MATCHED: Record<CrmDuplicate["matchedBy"][number], string> = {
  phone: "телефон",
  email: "почта",
  inn: "ИНН"
};
export function DuplicatesNotice(t0) {
  const $ = _c(18);
  const {
    duplicates,
    className
  } = t0;
  const {
    t
  } = useTranslation();
  if (duplicates.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-400", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AlertTriangle className="mt-0.5 size-4 shrink-0" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = duplicates.length === 1 ? "\u0435\u0441\u0442\u044C \u0435\u0449\u0451 \u043E\u0434\u043D\u0430 \u0437\u0430\u044F\u0432\u043A\u0430" : `есть ещё ${duplicates.length} заявки`;
  let t4;
  if ($[3] !== t3) {
    t4 = <p className="font-medium">Похоже на дубль: {t3}</p>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== duplicates || $[6] !== t) {
    let t6;
    if ($[8] !== t) {
      t6 = duplicate => <li key={duplicate.id} className="text-foreground"><Link to={`/applications/${duplicate.id}`} className="font-medium underline-offset-4 hover:underline">{duplicate.shopName}</Link><span className="text-muted-foreground">{" "}· {duplicate.contactName} ·{" "}{t(`applications.status.${duplicate.status}`, {
            defaultValue: duplicate.status
          })} · подана{" "}{formatDay(duplicate.submittedAt)} · совпали: {duplicate.matchedBy.map(_temp6).join(", ")}</span></li>;
      $[8] = t;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t5 = duplicates.map(t6);
    $[5] = duplicates;
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <ul className="mt-1 flex flex-col gap-0.5">{t5}</ul>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = <div className="min-w-0 flex-1">{t4}{t6}</div>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t1 || $[16] !== t7) {
    t8 = <div role="status" data-testid="crm-duplicates" className={t1}>{t2}{t7}</div>;
    $[15] = t1;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
function _temp6(key) {
  return MATCHED[key];
}